import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import V3Header from '../components/V3Header';
import V3Footer from '../components/V3Footer';
import { useLanguage } from '../LanguageContext';
import { submitToSheet } from '../formSubmit';
import '../V3.css';

const V3Apply = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [openModule, setOpenModule] = useState(null);

  const modules = t('apply.modules');
  const processSteps = t('apply.processSteps');

  useEffect(() => {
    const scrollToFormEl = () => {
      document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const maybeScroll = () => {
      if (window.location.hash === '#apply-form') {
        scrollToFormEl();
        setTimeout(scrollToFormEl, 300);
      }
    };

    maybeScroll();
    window.addEventListener('hashchange', maybeScroll);
    return () => window.removeEventListener('hashchange', maybeScroll);
  }, [location.pathname]);

  const scrollToForm = (e) => {
    e.preventDefault();
    document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', '/apply#apply-form');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitToSheet('ummeedvar', e.currentTarget);
    setSubmitted(true);
    document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="v3-app">
      <div className="v3-page">
        <V3Header
          primaryCta={(
            <>
              <a href="#apply-form" className="v3-btn primary" onClick={scrollToForm}>{t('common.becomeCandidateArrow')}</a>
              <Link to="/sponsor-a-program" className="v3-btn">{t('common.becomeSponsor')}</Link>
            </>
          )}
        />
        <div className="v3-hero">
          <h1>{t('apply.title')}</h1>
          <p>{t('apply.intro')}</p>
          <div className="v3-btn-row">
            <a href="#apply-form" className="v3-btn primary" onClick={scrollToForm}>{t('apply.formTitle')} ↓</a>
          </div>
        </div>

        <section className="v3-block">
          <h2>{t('apply.processTitle')}</h2>
          <div className="v3-grid4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
            {Array.isArray(processSteps) && processSteps.map((step, i) => (
              <div key={step} className="v3-step">
                <div className="v3-num">{i + 1}</div>
                {step}
              </div>
            ))}
          </div>
        </section>

        <section className="v3-block">
          <h2>{t('apply.syllabusTitle')}</h2>
          <div className="v3-sub">{t('apply.syllabusSub')}</div>
          <div className="v3-stack">
            {Array.isArray(modules) && modules.map(({ title, objective, topics }, i) => (
              <div key={title} className="v3-accordion-item">
                <button
                  type="button"
                  className={`v3-accordion-trigger ${openModule === i ? 'open' : ''}`}
                  onClick={() => setOpenModule(openModule === i ? null : i)}
                  aria-expanded={openModule === i}
                >
                  <strong>{title}</strong>
                  <span className="v3-accordion-icon">{openModule === i ? '−' : '+'}</span>
                </button>
                {openModule === i && (
                  <div className="v3-accordion-panel">
                    <p style={{ fontSize: 13, margin: '0 0 10px' }}>
                      <strong>{t('common.objective')}:</strong> {objective}
                    </p>
                    <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 6px' }}>{t('common.topics')}:</p>
                    <ul className="v3-wire-list" style={{ fontSize: 12, margin: 0 }}>
                      {topics.map((topic) => <li key={topic}>{topic}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, marginTop: 14 }}>
            {t('apply.sponsorLink')} <Link to="/sponsor-a-program">{t('apply.sponsorPageLink')}</Link>
          </p>
        </section>

        <section className="v3-block light-gray" id="apply-form">
          <h2>{t('apply.formTitle')}</h2>
          {submitted ? (
            <p style={{ fontSize: 15, color: '#333' }}>{t('apply.formSuccess')}</p>
          ) : (
            <form onSubmit={handleSubmit}>
              <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('apply.formBasicInfo')}</h3>
              <div className="v3-grid2">
                <div>
                  <div className="v3-form-field">
                    <label htmlFor="fullName">{t('apply.formFullName')}</label>
                    <input id="fullName" name="fullName" required />
                  </div>
                  <div className="v3-form-field">
                    <label htmlFor="age">{t('apply.formAge')}</label>
                    <input id="age" name="age" type="number" required />
                  </div>
                  <div className="v3-form-field">
                    <label htmlFor="education">{t('apply.formEducation')}</label>
                    <input id="education" name="education" required />
                  </div>
                  <div className="v3-form-field">
                    <label htmlFor="mobile">{t('apply.formMobile')}</label>
                    <input id="mobile" name="mobile" type="tel" required />
                  </div>
                  <div className="v3-form-field">
                    <label htmlFor="email">{t('apply.formEmail')}</label>
                    <input id="email" name="email" type="email" required />
                  </div>
                </div>
                <div>
                  <div className="v3-form-field">
                    <label htmlFor="state">{t('apply.formState')}</label>
                    <input id="state" name="state" required />
                  </div>
                  <div className="v3-form-field">
                    <label htmlFor="district">{t('apply.formDistrict')}</label>
                    <input id="district" name="district" required />
                  </div>
                  <div className="v3-form-field">
                    <label htmlFor="panchayat">{t('apply.formPanchayat')}</label>
                    <input id="panchayat" name="panchayat" required />
                  </div>
                </div>
              </div>
              <h3 style={{ fontSize: 15, margin: '20px 0 12px' }}>{t('apply.formExtraInfo')}</h3>
              <div className="v3-form-field">
                <label htmlFor="socialWork">{t('apply.formSocialWork')}</label>
                <input id="socialWork" name="socialWork" />
              </div>
              <div className="v3-form-field">
                <label htmlFor="motivation">{t('apply.formMotivation')}</label>
                <textarea id="motivation" name="motivation" />
              </div>
              <button type="submit" className="v3-btn primary" style={{ width: 200 }}>
                {t('apply.formSubmit')}
              </button>
            </form>
          )}
        </section>
        <V3Footer />
      </div>
    </div>
  );
};

export default V3Apply;
